import * as THREE from 'three';
import type { InteractionData } from '../types';

// ============================================
// Interactable - Objeto interactivo base
// ============================================

export interface InteractableOptions {
  id: string;
  label: string;
  position: { x: number; y: number; z: number };
  radius?: number;
  isActive?: boolean;
  onInteract?: () => void;
  onHover?: () => void;
  onUnhover?: () => void;
  requiredItem?: string;
  oneShot?: boolean;
}

export class Interactable {
  readonly id: string;
  readonly label: string;
  readonly position: THREE.Vector3;
  readonly radius: number;
  isActive: boolean;
  oneShot: boolean;

  private onInteractCallback?: () => void;
  private onHoverCallback?: () => void;
  private onUnhoverCallback?: () => void;
  private hasBeenInteracted = false;
  private mesh: THREE.Object3D | null = null;

  constructor(options: InteractableOptions) {
    this.id = options.id;
    this.label = options.label;
    this.position = new THREE.Vector3(
      options.position.x,
      options.position.y,
      options.position.z
    );
    this.radius = options.radius ?? 2;
    this.isActive = options.isActive ?? true;
    this.oneShot = options.oneShot ?? false;
    this.onInteractCallback = options.onInteract;
    this.onHoverCallback = options.onHover;
    this.onUnhoverCallback = options.onUnhover;
  }

  setMesh(mesh: THREE.Object3D): void {
    this.mesh = mesh;
  }

  getMesh(): THREE.Object3D | null {
    return this.mesh;
  }

  canInteract(): boolean {
    if (!this.isActive) return false;
    if (this.oneShot && this.hasBeenInteracted) return false;
    return true;
  }

  interact(): void {
    if (!this.canInteract()) return;

    this.hasBeenInteracted = true;
    this.onInteractCallback?.();
  }

  hover(): void {
    this.onHoverCallback?.();
  }

  unhover(): void {
    this.onUnhoverCallback?.();
  }

  getDistanceTo(point: THREE.Vector3): number {
    return this.position.distanceTo(point);
  }

  toInteractionData(): InteractionData {
    return {
      id: this.id,
      label: this.label,
      position: {
        x: this.position.x,
        y: this.position.y,
        z: this.position.z,
      },
      radius: this.radius,
      isActive: this.isActive,
      onInteract: this.onInteractCallback,
    };
  }

  reset(): void {
    this.hasBeenInteracted = false;
    this.isActive = true;
  }
}
